import React, { useEffect, useRef, useState } from 'react';
import Image from '../fragments/Image';
import SpecialText from '../fragments/SpecialText';
import { useReveal } from '../hooks/UseReveal';

export default function Splash({ post }) {
	const splashRef = useReveal({ once: true });
	const parallaxRef = useRef(null);
	const elementOffsetTopRef = useRef(null);
	const videoRef = useRef(null);
	const [isPortrait, setIsPortrait] = useState(false);
	const [isMounted, setIsMounted] = useState(false);

	useEffect(() => {
		setIsMounted(true);
		const checkOrientation = () => {
			setIsPortrait(window.innerHeight > window.innerWidth);
		};

		checkOrientation();
		window.addEventListener('resize', checkOrientation);

		return () => window.removeEventListener('resize', checkOrientation);
	}, []);

	// Intersection Observer for video playback
	useEffect(() => {
		if (!isMounted || !videoRef.current) return;

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						videoRef.current?.play();
					} else {
						videoRef.current?.pause();
					}
				});
			},
			{ threshold: 0.25 } // Play when 25% visible
		);

		observer.observe(videoRef.current);

		return () => {
			if (videoRef.current) {
				observer.unobserve(videoRef.current);
			}
		};
	}, [isMounted, isPortrait]);

	useEffect(() => {
		if (!isMounted || !parallaxRef.current) return;

		const parallaxElement = parallaxRef.current;
		const speed = 0.5;

		// Calculate true offset from document top by recursing through offsetParent
		const calculateOffset = () => {
			let offset = 0;
			let element = parallaxElement;
			while (element) {
				offset += element.offsetTop;
				element = element.offsetParent;
			}
			elementOffsetTopRef.current = offset;
		};

		const handleScroll = () => {
			if (elementOffsetTopRef.current === null) return;
			const scrollPos = window.lenis?.scroll || window.scrollY || 0;
			const offset = (scrollPos - elementOffsetTopRef.current) * speed;
			parallaxElement.style.transform = `translate3d(0, ${offset}px, 0)`;
		};

		const handleResize = () => {
			calculateOffset();
			handleScroll();
		};

		// Initial calculation
		requestAnimationFrame(() => {
			calculateOffset();
			handleScroll();
		});

		// Listen to Lenis scroll event
		window.lenis?.on('scroll', handleScroll);
		// Fallback for regular scroll
		window.addEventListener('scroll', handleScroll);
		window.addEventListener('resize', handleResize);

		return () => {
			window.lenis?.off('scroll', handleScroll);
			window.removeEventListener('scroll', handleScroll);
			window.removeEventListener('resize', handleResize);
		};
	}, [isMounted]);

	const landscapeImage = post?.splash_landscape_image || [];
	const portraitImage = post?.splash_portrait_image || [];
	const landscapeAlt = post?.splash_landscape_image_alt || '';
	const portraitAlt = post?.splash_portrait_image_alt || '';
	const landscapeCinemagraph = post?.splash_landscape_cinemagraph || '';
	const portraitCinemagraph = post?.splash_portrait_cinemagraph || '';

	const imageSrc = isPortrait ? portraitImage : landscapeImage;
	const imageAlt = isPortrait ? portraitAlt : landscapeAlt;
	const cinemagraphSrc = isPortrait ? portraitCinemagraph : landscapeCinemagraph;

	const hasImageForOrientation = Array.isArray(imageSrc) && imageSrc.some(Boolean);
	const hasVideoForOrientation = Boolean(cinemagraphSrc);
	const hasRenderableContent = Boolean(post) && (hasVideoForOrientation || hasImageForOrientation);

	useEffect(() => {
		if (!isMounted || typeof document === 'undefined') return;

		const bodyClass = 'state-no-splash';
		if (!hasRenderableContent) {
			document.body.classList.add(bodyClass);
		} else {
			document.body.classList.remove(bodyClass);
		}

		return () => {
			document.body.classList.remove(bodyClass);
		};
	}, [isMounted, hasRenderableContent]);

	if (!hasRenderableContent) return null;

	const textColor = post?.splash_text_color || '#000';

	return (
		<div className="c-splash c-transition-in" ref={splashRef}>
			<div className="c-splash__media">
				<div className="c-image-parallax" ref={parallaxRef}>
					{cinemagraphSrc ? (
						<video
							ref={videoRef}
							src={cinemagraphSrc}
							muted
							loop
							playsInline
							className="c-splash__video"
						/>
					) : (
						<Image 
							src={imageSrc} 
							alt={imageAlt}
						/>
					)}
				</div>
			</div>
			{post?.splash_text && (
				<h1 className="c-splash__text" style={{ color: textColor }}>
					<SpecialText text={post.splash_text} />
				</h1>
			)}
		</div>
	);
}